import type { ReasoningEffort, SkillVersions } from '@elara/domain/flows';

import type { Disposer } from '@elara/core/types';
import type { ClientMessagePart, MessagePart } from '@elara/domain/models/schemas/message-schemas';

import type { ChatsContainer } from '#containers/ChatsContainer.js';
import type { KeyValueStore } from '#lib/key-value-store.js';
import type { ChatMessage } from '#models/ChatMessageModel.js';
import type { Chat } from '#models/ChatModel.js';
import type { ResourceMetadata } from '#models/ResourceMetadataModel.js';
import type { ToolCall } from '#models/ToolCallModel.js';
import type { QueryHandle } from '../firebase/types.js';
import type { NavigationPort } from './navigation-port.js';
import type { SessionState } from './SessionState.js';
import type { ToastState } from './ToastState.js';
import type { TripsState } from './TripsState.js';
import type { UserProfileState } from './UserProfileState.js';

import { effect } from '@preact/signals-react';
import { groupBy } from 'lodash-es';

import { isAnsweredQuestionsPart } from '@elara/domain/models/ChatMessageModelSpec';
import { isDataPart, isMediaPart, isTextPart } from '@elara/domain/models/schemas/message-schemas';
import { orderBy, where } from '@elara/firebolt/shared';

import { ROUTES } from '#constants.js';
import { trackResponseStopped } from '#lib/analytics-events.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { EMPTY_QUERY } from '#lib/firebolt-utils.js';
import { createState } from '#lib/signals-utils.js';
import { generatePageTitle } from '#lib/page-title.js';

import { clearChatDraft } from './chat-draft-storage.js';
import { isTripRoutePath } from './routing-contract.js';

function compareMessages(a: ChatMessage, b: ChatMessage): number {
  const createdAtDiff = a.createdAt.getTime() - b.createdAt.getTime();
  if (createdAtDiff !== 0) return createdAtDiff;

  const sequenceDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
  if (sequenceDiff !== 0) return sequenceDiff;

  return a.id.localeCompare(b.id);
}

function isResendableUserPart(part: MessagePart): part is ClientMessagePart {
  if (part.temporary) return false;
  return isTextPart(part) || isMediaPart(part) || isDataPart(part);
}

/**
 * The main reactive store for all core chat functionality.
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createChatState(
  sessionState: SessionState,
  chats: ChatsContainer,
  routerState: NavigationPort,
  toastState: ToastState,
  tripsState: TripsState,
  userProfileState: UserProfileState,
  /** Backs the per-chat composer drafts this store prunes when a chat is archived (ELARA-969). */
  keyValueStore: KeyValueStore,
) {
  const { ClientToolCall, ClientResourceMetadata, ClientChat, ClientChatMessage, modelToClientPipeline } = chats;

  // Chat ids last seen in this trip's list, for the draft prune below (ELARA-969). Plain closure
  // variables, not state fields: the prune effect both reads and writes them, and a signal would
  // re-trigger the effect that wrote it.
  let draftPruneTripId: string | undefined;
  let seenChatIds: string[] = [];

  const state = createState({
    abortController: new AbortController(),
    /** The currently active/selected chat instance. `undefined` if no chat is selected. */
    current: undefined as Chat | undefined,
    /** The ID of the message that is currently receiving a streamed response. */
    streamingMessageId: undefined as string | undefined,
    /** The accumulated text content of the current streaming response. */
    streamingContent: '',
    /** Collection of cleanup functions for all effects (instance-scoped) */
    cleanupFunctions: [] as Disposer[],
    /** Cleanup function reference to ensure idempotency (instance-scoped) */
    cleanupFunctionRef: null as Disposer | null,
    /** Idempotency guard for `startSyncEffects()`, separate from `cleanupFunctionRef` so a
     * screen-driven caller (mobile) can start just the sync effects without `startMonitors()`. */
    syncCleanupFunctionRef: null as Disposer | null,
    /** Track previous trip ID to detect trip changes */
    _previousTripId: undefined as string | undefined,
    /** Track previous chat ID to detect chat changes for query cleanup */
    _previousChatId: undefined as string | undefined,
    /** Previous query handles for cleanup on trip/chat switch */
    _prevRecentChatsQuery: undefined as QueryHandle<Chat> | undefined,
    _prevMessagesQuery: undefined as QueryHandle<ChatMessage> | undefined,
    _prevToolCallsQuery: undefined as QueryHandle<ToolCall> | undefined,
    debugResourceQueries: {} as Record<string, QueryHandle<ResourceMetadata>>,

    /** An array to hold tool call requests and responses for the current message exchange. */
    get toolCallsQuery(): QueryHandle<ToolCall> {
      const chat = this.current;
      if (!chat?.id) return EMPTY_QUERY;
      // The `userId` equality is load-bearing, not a redundant narrowing of `chatId`: the traveler
      // path through the `tools` security rule authorizes this collection-group LIST via
      // `resource.data.userId == request.auth.uid`, and Firestore can only satisfy a
      // `resource.data`-dependent list rule when the query itself carries that equality. Without it
      // every non-staff session got `permission-denied` (retried 5x, then "Exhausted snapshot
      // retries"), so tool badges never rendered for real clients.
      // Filtered on the CHAT owner rather than the signed-in uid so privileged staff reading another
      // traveler's chat still match their documents; their access comes from `hasPrivileges()`.
      const toolCalls = ClientToolCall.listCollectionGroup(
        'tools',
        where('chatId', '==', chat.id),
        where('userId', '==', chat.userId),
        orderBy('createdAt', 'asc'),
      );
      return toolCalls;
    },

    get toolCalls(): ToolCall[] {
      return this.toolCallsQuery.items;
    },

    get hasToolErrors(): boolean {
      return this.toolCalls.some((tc) => tc.hasFailed);
    },

    get toolCallsByMessageId(): Record<string, ToolCall[]> {
      return groupBy(this.toolCalls, (tc) => tc.messageId);
    },
    resourceMetadataQuery(messageId: string): QueryHandle<ResourceMetadata> {
      if (!this.current?.id) return EMPTY_QUERY;
      // Only query resources if user has privileges (dev/admin/@elara.travel email)
      if (!userProfileState.isPrivileged) return EMPTY_QUERY;
      const cached = this.debugResourceQueries[messageId];
      if (cached) return cached;

      // Evict oldest entries to cap active debug Firestore listeners
      const MAX_DEBUG_RESOURCE_QUERIES = 20;
      const keys = Object.keys(this.debugResourceQueries);
      if (keys.length >= MAX_DEBUG_RESOURCE_QUERIES) {
        const toEvict = keys.slice(0, keys.length - MAX_DEBUG_RESOURCE_QUERIES + 1);
        for (const key of toEvict) {
          this.debugResourceQueries[key]?.unsubscribe?.();
          delete this.debugResourceQueries[key];
        }
      }

      const query = ClientResourceMetadata.list({ chatId: this.current.id, messageId }, orderBy('index', 'asc'));
      this.debugResourceQueries[messageId] = query;
      return query;
    },
    /** ID returned from API for the optimistic message */
    optimisticMessageId: undefined as string | undefined,
    /** Reply currently waiting for an accepted Stop request to settle in the live message state. */
    stoppingReplyId: undefined as string | undefined,
    /** A computed signal that returns `true` if a chat has been loaded. */
    get hasLoaded(): boolean {
      return !!this.current;
    },
    /**
     * A computed signal that returns `true` while a concierge reply is in progress — so the Stop
     * button appears the instant the user sends and stays until the reply completes.
     *
     * Keys off the optimistic model reply being in-flight (`isOptimistic`, i.e. 'receiving' →
     * 'streaming'), which is pushed the moment the message is sent — so the button shows immediately,
     * before the first token, and doesn't flicker if the transport delivers the whole reply at once
     * (e.g. React Native, where SSE may not surface incrementally). After a page reload there is no
     * optimistic object, so it also covers a reply the server is still generating, detected from
     * persisted metadata (`msg.isGenerating`).
     */
    get isStreaming(): boolean {
      return this.messagesList.some((msg: ChatMessage) => msg.isModelMessage && (msg.isOptimistic || msg.isGenerating));
    },
    /**
     * True from the instant Stop is pressed until that exact reply leaves its optimistic/generating
     * state. This intentionally outlives the StopGeneration HTTP request: the server may accept the
     * request before the generation worker observes the persisted marker and closes its stream.
     */
    get isStopping(): boolean {
      if (!this.stoppingReplyId) return false;
      return this.messagesList.some(
        (msg: ChatMessage) =>
          msg.id === this.stoppingReplyId && msg.isModelMessage && (msg.isOptimistic || msg.isGenerating),
      );
    },
    /**
     * A computed signal that returns a reactive query for the chats in the current trip.
     *
     * When viewing another user's trip (privileged access), queries chats by the trip owner's
     * userId, not the current admin's userId. This allows admins/devs to view other users'
     * trip chats for debugging purposes.
     */
    get recentChats(): QueryHandle<Chat> {
      if (!sessionState.userId || !tripsState.current) return EMPTY_QUERY;

      // Determine the target userId for the query:
      // - For privileged users viewing another user's trip, use the trip owner's userId
      // - Otherwise, use the current user's userId
      const targetUserId = tripsState.current.userId;

      // Guard: Only proceed if we have a target user
      if (!targetUserId) return EMPTY_QUERY;

      // Guard: Non-privileged users can only see their own trips' chats
      if (targetUserId !== sessionState.userId && !sessionState.isPrivileged) {
        console.warn("[ChatsState] Non-privileged user attempting to access another user's chats");
        return EMPTY_QUERY;
      }

      return ClientChat.list(
        {},
        where('userId', '==', targetUserId),
        where('tripId', '==', tripsState.current.id),
        where('status', '!=', 'archived'),
        orderBy('status', 'asc'),
        orderBy('createdAt', 'desc'),
      );
    },
    /** Get the current messages query */
    get messages(): QueryHandle<ChatMessage> {
      if (!this.current?.id || !sessionState.userId) return EMPTY_QUERY;
      return ClientChatMessage.list({ chatId: this.current.id });
    },
    /**
     * True once `current` can be trusted as *this trip's* chat — either it's confirmed to be one of
     * `recentChats.items`, or `recentChats` has resolved and the trip genuinely has none yet. `EMPTY_QUERY`
     * is always `isReady: true`, so `messages.isReady` alone can't distinguish "no chat selected yet /
     * still the previous trip's stale chat" from "chat selected, confirmed zero messages" — every
     * consumer that branches on chat emptiness (`messagesList`, `ChatInputState.isChatEmpty`, the
     * web/mobile message views) needs this gate first. Matters most where trip/chat selection is
     * screen-driven rather than route-driven (mobile, ELARA-738/739): `recentChats` can flip `isReady`
     * one render before `current` is reassigned to match, and a stale-but-still-defined `current` from a
     * just-abandoned trip looks exactly like a confirmed-empty chat without this check.
     */
    get isSelectionSettled(): boolean {
      if (!tripsState.current) return false;
      const chats = this.recentChats;
      if (!chats.isReady) return false;
      if (chats.items.length === 0) return true;
      return this.current !== undefined && chats.items.some((c) => c.id === this.current!.id);
    },
    get lastMessage(): ChatMessage | undefined {
      return this.messagesList.at(-1);
    },
    /**
     * Returns the merged list of loaded messages and in-flight messages.
     * This getter is pure - side effects (like messageHasLanded) are handled by effects in startMonitors().
     */
    get messagesList(): ChatMessage[] {
      const query = this.messages;
      if (!query.isReady && !this.current?.messagesInFlight.length) {
        return [];
      }
      const loaded = query.items.filter((msg) => msg.deleted !== true);
      const inFlight = this.current?.messagesInFlight ?? [];
      const activeInFlight = inFlight.filter(
        (msg) => msg.__optimisticState !== undefined && !query.items.some((l) => l.id === msg.id),
      );
      const items = [...loaded, ...activeInFlight].toSorted(compareMessages);
      return items;
    },
    get messagesById(): Record<string, ChatMessage> {
      return this.messagesList.reduce(
        (acc: Record<string, ChatMessage>, msg: ChatMessage) => {
          acc[msg.id] = msg;
          return acc;
        },
        {} as Record<string, ChatMessage>,
      );
    },
    /**
     * Get answers from user messages that come AFTER the given message.
     *
     * For a given model message containing an AskUserWidget, this function
     * looks at subsequent user messages to find AnsweredQuestionsParts.
     * Only the FIRST subsequent user message with answers is considered.
     *
     * @param messageId - The model message ID containing the AskUserWidget
     * @returns Record of question text -> answer (single string or array for multi-select)
     */
    getAnswersAfterMessage(messageId?: string): Record<string, string | string[]> {
      if (!messageId) return {};

      const messages = this.messagesList;
      const messageIndex = messages.findIndex((m) => m.id === messageId);
      if (messageIndex === -1) return {};

      // Look at all user messages AFTER this message
      const result: Record<string, string | string[]> = {};
      for (let i = messageIndex + 1; i < messages.length; i++) {
        const msg = messages[i];
        if (!msg) continue;

        if (msg.isUserMessage && msg.content) {
          for (const part of msg.content) {
            if (isAnsweredQuestionsPart(part)) {
              Object.assign(result, part.data.answeredQuestions);
            }
          }
          // Only look at the FIRST subsequent user message with answers
          if (Object.keys(result).length > 0) break;
        }
      }
      return result;
    },
    /** A computed signal that constructs the `MessagePart` array for the in-progress streaming message. */
    get streamingParts(): MessagePart[] | undefined {
      if (!this.isStreaming) return;
      if (!this.streamingMessageId) return;
      return [{ text: this.streamingContent || '...' }];
    },
    /** A computed signal that returns the Firebase Storage path for attachments for the current chat. */
    get attachmentStoragePath(): string | undefined {
      if (!this.current?.id) return;
      return `chats/${this.current.id}/attachments`;
    },
    /** Selects a chat by ID from the recentChats list */
    selectChatById(chatId: string): boolean {
      if (this.recentChats.isReady) {
        if (this.current?.id === chatId) return true;
        else this.reset();
        const chat = this.recentChats.items.find((c) => c.id === chatId);
        if (chat) {
          this.current = chat;
          return true;
        }
      }
      return false;
    },
    reset(): void {
      this.streamingContent = '';
      this.streamingMessageId = undefined;

      for (const query of Object.values(this.debugResourceQueries)) query.unsubscribe?.();
      this.debugResourceQueries = {};
      // Clear current chat when resetting (e.g., when switching trips)
      this.current = undefined;
    },
    clearCurrentChat(): void {
      this.current = undefined;
      // Update URL to reflect no chat selected
      routerState.navigate(ROUTES.HOME);
    },
    lastSavedMessageSequence(): number {
      if (!this.current?.id) throw new Error('No chat selected');
      return this.current.messageSequence ?? 0;
    },
    _nextMessageSequence: 0,
    nextMessageSequence(): number {
      this._nextMessageSequence = this.lastSavedMessageSequence() + 1;
      return this._nextMessageSequence;
    },
    /** Get currently active tool executions */
    get activeToolCalls(): ToolCall[] {
      return this.toolCalls.filter((tc) => tc.status === 'executing' || tc.status === 'pending');
    },
    /** Handles the logic for deleting (archiving) a chat session. */
    async deleteChat(chatId: string): Promise<void> {
      try {
        // If this is the currently selected chat, clear it first
        if (this.current?.id === chatId) {
          this.clearCurrentChat();
        }

        // Call the delete flow
        await ClientChat.delete({ chatId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Handles the logic for restoring a previously archived chat session. */
    async restoreChat(chatId: string): Promise<void> {
      try {
        await ClientChat.restore({ chatId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Deletes a message from the current chat (privileged users only). */
    async deleteMessage(messageId: string): Promise<void> {
      try {
        const chatId = this.current?.id;
        if (!chatId) {
          throw new Error('No chat selected');
        }
        await ClientChat.deleteMessage({ chatId, messageId });
      } catch (error) {
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to delete message',
          description: 'Unable to delete this message right now. Please try again.',
          variant: 'destructive',
        });
        throw error;
      }
    },

    /**
     * Interrupts the in-progress AI reply. Prefers the live optimistic reply; after a refresh (no
     * optimistic object and no live stream) it falls back to the in-progress reply loaded from
     * Firestore (`isGenerating`). Server-driven: it asks the server to stop and lets the SSE stream
     * (if still connected) end cleanly — we don't abort the local fetch, avoiding a render flicker.
     * The persisted partial reply then reconciles via the Firestore listener.
     */
    async stopStreaming(): Promise<string | undefined> {
      const chat = this.current;
      if (!chat || !this.isStreaming || this.isStopping) return;
      const reply =
        chat.messagesInFlight.find((msg) => msg.isModelMessage && msg.isOptimistic) ??
        this.messagesList.find((msg) => msg.isModelMessage && msg.isGenerating);
      if (!reply) return;
      const source = reply.replyTo ? this.messagesById[reply.replyTo] : undefined;
      const originalText = source?.content.filter(isTextPart).map((part) => part.text).join('\n') ?? '';

      this.stoppingReplyId = reply.id;
      // hadContent is a safe boolean (whether any text streamed before the stop), never the content
      // itself — keeps the analytics event PII-free (ids + a low-cardinality boolean only).
      const hadContent = reply.content.length > 0;
      try {
        const result = await chat.stopGeneration(reply.id);
        if (!result.stopped) {
          this.stoppingReplyId = undefined;
          return;
        }
        trackResponseStopped({ chatId: chat.id, tripId: tripsState.current?.id, hadContent });
        if (result.preGeneration) {
          this.stoppingReplyId = undefined;
          if (reply.replyTo && this.current?.id === chat.id) {
            return originalText;
          }
        }
        return;
      } catch (error) {
        // Do not let a late failure from a chat the user has since left clear a newer reply's
        // pending state.
        if (this.stoppingReplyId === reply.id) {
          this.stoppingReplyId = undefined;
        }
        // Best-effort: a failed stop (the reply already finished, a transient network error, or a
        // backend without the StopGeneration flow yet) must never surface as a fatal error — the
        // worst case is the Stop button becoming available for retry. Log at warning level, not
        // captureException.
        getErrorReporter().captureMessage(`Stop generation request failed: ${String(error)}`, { level: 'warning' });
        return;
      }
    },

    /** Returns whether a message is eligible for resend (must be the latest user message in the chat). */
    canResendMessage(messageId: string): boolean {
      const message = this.messagesById[messageId];
      if (!message || !message.isUserMessage) return false;
      return this.lastMessage?.id === messageId;
    },

    /** Deletes a user message and immediately re-sends it as a new user turn. */
    async resendMessage(
      messageId: string,
      {
        modelId,
        promptVersion,
        reasoningEffort,
        skillVersions,
      }: {
        modelId?: string;
        promptVersion?: number;
        reasoningEffort?: ReasoningEffort;
        skillVersions?: SkillVersions;
      } = {},
    ): Promise<void> {
      try {
        const chat = this.current;
        if (!chat) {
          throw new Error('No chat selected');
        }

        const sourceMessage = this.messagesById[messageId];
        if (!sourceMessage) {
          throw new Error('Message not found');
        }
        if (!sourceMessage.isUserMessage) {
          throw new Error('Only user messages can be resent');
        }
        if (!this.canResendMessage(messageId)) {
          throw new Error('Only the latest user message can be resent');
        }

        const resendableContent: ClientMessagePart[] = sourceMessage.content
          .filter(isResendableUserPart)
          .map((part) => ({ ...part }));

        if (resendableContent.length === 0) {
          throw new Error('Message has no resendable content');
        }

        await ClientChat.deleteMessage({ chatId: chat.id, messageId });

        await chat.sendMessage(
          {
            role: 'user',
            userId: chat.userId,
            chatId: chat.id,
            content: resendableContent,
            sequence: this.nextMessageSequence(),
          },
          { abortSignal: this.abortController.signal, modelId, promptVersion, reasoningEffort, skillVersions },
        );
      } catch (error) {
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Failed to resend message',
          description: 'Unable to resend this message right now. Please try again.',
          variant: 'destructive',
        });
        throw error;
      }
    },

    /** Handle route changes - now focused on chat selection within current trip */
    handleRouteChange(route: { path: string; params: Record<string, string> }): void {
      // Check if we're on the trip page (bare /trip/:id or the panel form /trip/:id/:panel, ELARA-704)
      const isTripRoute = isTripRoutePath(route.path);

      if (isTripRoute && route.params.id && tripsState.current) {
        const tripId = route.params.id;

        // If we're on a trip route and have a current trip, select the primary chat
        if (tripsState.current.id === tripId) {
          // Select the first available chat for this trip
          if (this.recentChats.isReady && this.recentChats.items.length > 0) {
            const primaryChat = this.recentChats.items[0]; // First chat is primary
            if (primaryChat && this.current?.id !== primaryChat.id) {
              this.current = primaryChat;
            }
          }
        }
      } else {
        // Clear current chat if not on a trip route or no trip selected
        this.current = undefined;
      }
    },

    /**
     * Registers the trip/chat-switch bookkeeping effects: reset stale `current` + unsubscribe old
     * Firestore listeners when the trip or chat changes, land in-flight messages once their real doc
     * arrives, and reset the model-to-client pipeline tracker on chat switch. None of this depends on
     * `routerState` — it's split out from `startMonitors()` so a screen-driven caller (mobile, which
     * selects the trip/chat via params rather than a route) can start it directly without also pulling
     * in the route-change/page-title effects below, which assume a web-style URL route. Idempotent.
     */
    startSyncEffects(): Disposer {
      if (this.syncCleanupFunctionRef) {
        return this.syncCleanupFunctionRef;
      }

      const effects = [
        // Effect: Reset chat state and clean up stale Firestore listeners when trip changes
        effect(() => {
          const currentTrip = tripsState.current;
          const currentTripId = currentTrip?.id;
          // Only reset if the trip actually changed (not on initial load)
          if (this._previousTripId !== undefined && this._previousTripId !== currentTripId) {
            // Capture previous query handles before reset clears this.current
            const prevRecentChats = this._prevRecentChatsQuery;
            const prevMessages = this._prevMessagesQuery;
            const prevToolCalls = this._prevToolCallsQuery;

            this.reset();

            // Unsubscribe old queries to stop zombie Firestore listeners
            if (prevRecentChats && prevRecentChats !== EMPTY_QUERY) prevRecentChats.unsubscribe?.();
            if (prevMessages && prevMessages !== EMPTY_QUERY) prevMessages.unsubscribe?.();
            if (prevToolCalls && prevToolCalls !== EMPTY_QUERY) prevToolCalls.unsubscribe?.();
          }
          // Capture current query handles for next cleanup cycle
          this._prevRecentChatsQuery = currentTripId ? this.recentChats : undefined;
          this._previousTripId = currentTripId;
        }),
        // Effect: Clear the persisted composer draft of any chat that vanishes from this trip's chat
        // list. `recentChats` filters out archived chats, so a chat disappearing means it was
        // archived — by `deleteChat`, or by the cascade in `DeleteTripFlow` — and its draft can
        // never be reached again (ELARA-969).
        //
        // Only prunes against a ready, non-empty query for the SAME trip. `recentChats` also
        // returns EMPTY_QUERY on a trip switch, on sign-out, and while loading; treating any of
        // those as "every chat was archived" would wipe drafts the user is still writing.
        effect(() => {
          const tripId = tripsState.current?.id;
          const query = this.recentChats;
          if (!tripId || query === EMPTY_QUERY || !query.isReady) return;

          const chatIds = query.items.map((chat: Chat) => chat.id);
          if (draftPruneTripId === tripId) {
            for (const seenId of seenChatIds) {
              if (!chatIds.includes(seenId)) clearChatDraft(keyValueStore, seenId);
            }
          }
          draftPruneTripId = tripId;
          seenChatIds = chatIds;
        }),
        // Effect: Clean up stale message/toolCall listeners when switching chats within a trip
        effect(() => {
          const currentChatId = this.current?.id;
          if (this._previousChatId !== undefined && this._previousChatId !== currentChatId) {
            if (this._prevMessagesQuery && this._prevMessagesQuery !== EMPTY_QUERY) {
              this._prevMessagesQuery.unsubscribe?.();
            }
            if (this._prevToolCallsQuery && this._prevToolCallsQuery !== EMPTY_QUERY) {
              this._prevToolCallsQuery.unsubscribe?.();
            }
          }
          this._prevMessagesQuery = currentChatId ? this.messages : undefined;
          this._prevToolCallsQuery = currentChatId ? this.toolCallsQuery : undefined;
          this._previousChatId = currentChatId;
        }),
        effect(() => {
          const loaded = this.messages.items;
          const inFlight = this.current?.messagesInFlight ?? [];
          for (const msg of inFlight) {
            if (msg && loaded.some((l) => l?.id === msg.id)) {
              this.current?.messageHasLanded(msg, loaded);
            }
          }
        }),
        effect(() => {
          const chatId = this.current?.id;
          modelToClientPipeline.reset();
          if (chatId) {
            console.debug(`[ChatState] Reset pipeline tracker for chat: ${chatId}`);
          }
        }),
      ];

      this.cleanupFunctions.push(...effects);
      this.syncCleanupFunctionRef = () => {
        for (const cleanup of effects) cleanup();
      };
      return this.syncCleanupFunctionRef;
    },

    /** Start monitoring route changes (idempotent - safe to call multiple times) */
    startMonitors(): Disposer {
      // Return existing cleanup function if already started
      if (this.cleanupFunctionRef) {
        return this.cleanupFunctionRef;
      }

      this.startSyncEffects();

      // Register the route-dependent effects
      const effects = [
        // Effect 1: Handle route changes and chat selection
        effect(() => {
          const route = routerState.currentRoute;
          if (route) {
            this.handleRouteChange(route);
          }
        }),
        // Effect 2: Update page title when current chat changes
        effect(() => {
          const chat = this.current;
          if (chat) {
            routerState.setPageTitle(generatePageTitle(tripsState.current?.displayName || chat.displayName || ''));
          }
        }),
      ];

      this.cleanupFunctions.push(...effects);

      // Store and return cleanup function reference
      this.cleanupFunctionRef = () => this.stopMonitors();
      return this.cleanupFunctionRef;
    },

    /** Clean up all effects and stale Firestore listeners to prevent memory leaks */
    stopMonitors(): void {
      // Unsubscribe any tracked queries
      if (this._prevRecentChatsQuery && this._prevRecentChatsQuery !== EMPTY_QUERY) {
        this._prevRecentChatsQuery.unsubscribe?.();
        this._prevRecentChatsQuery = undefined;
      }
      if (this._prevMessagesQuery && this._prevMessagesQuery !== EMPTY_QUERY) {
        this._prevMessagesQuery.unsubscribe?.();
        this._prevMessagesQuery = undefined;
      }
      if (this._prevToolCallsQuery && this._prevToolCallsQuery !== EMPTY_QUERY) {
        this._prevToolCallsQuery.unsubscribe?.();
        this._prevToolCallsQuery = undefined;
      }
      for (const cleanup of this.cleanupFunctions) cleanup();
      this.cleanupFunctions.length = 0; // Clear the array
      this.cleanupFunctionRef = null; // Clear the reference
      this.syncCleanupFunctionRef = null; // Clear the sync-effects reference too — same cleanupFunctions array
    },
  });

  return state;
}

/** The main reactive store for all core chat functionality. All properties are signals transparent to the user. */
export type ChatsState = ReturnType<typeof createChatState>;
